// Global API interceptor for:
// 1. Session expiry handling (401 responses mean invalid/expired token)
// 2. CSRF token injection for state-changing requests
// 3. Cookie-based authentication (credentials: 'include')
//
// This app has no central API client (fetch calls are ad-hoc across every
// component), so wrapping window.fetch once here catches all requests.
//
// 401 responses with credentials: 'include' indicate an invalid/expired
// session -- signal to AuthContext to clear user state.

import { API_BASE_URL } from './config';
import { getSessionToken, rememberSessionToken, clearSessionToken, isNativeApp } from './nativeSession';

export const SESSION_EXPIRED_EVENT = 'acymart:session-expired';

let installed = false;
let csrfToken = null;

// The one token fetch that may be in flight at a time.
//
// Without this, two state-changing calls made in the same tick -- which is the
// normal case on a page load, where the visit ping and the cart fetch go out
// together -- both saw a null csrfToken and both issued their own
// GET /api/csrf-token. csurf with `cookie: true` mints a FRESH SECRET on every
// one of those and sets a new _csrf cookie, so the second response silently
// replaced the first request's cookie. Whichever request then posted the older
// token was validated against the newer cookie and came back
// "CSRF token invalid" -- a 403 on /api/traffic/visit and /api/cart that
// depended entirely on how the two requests interleaved, which is why it
// reproduced in a browser and never with a sequential client.
//
// Sharing the promise means every caller waits on the same request and ends up
// with the token that matches the cookie the browser actually holds.
let csrfInFlight = null;

// Fetch CSRF token from the backend.
//
// Goes through API_BASE_URL like every other call in the app rather than a
// bare relative path. A relative '/api/csrf-token' resolves against the page's
// own origin, which is only the backend in a same-origin deployment -- split
// frontend/backend hosts (and the Capacitor mobile build, where
// REACT_APP_API_URL is mandatory because there's no origin to be same-origin
// with) would fetch the SPA's own 404 page, leave csrfToken null, and send
// every POST/PUT/PATCH/DELETE without a token, so they'd all 403.
async function fetchCsrfToken(rawFetch) {
  if (csrfToken) return csrfToken;
  // Someone else is already asking. Wait for their answer rather than starting
  // a second mint that would invalidate theirs.
  if (csrfInFlight) return csrfInFlight;

  csrfInFlight = (async () => {
    try {
      // Uses the un-wrapped fetch: this runs *inside* the wrapper below, and
      // calling the patched window.fetch here would recurse on itself.
      const response = await rawFetch(`${API_BASE_URL}/api/csrf-token`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        csrfToken = data.csrfToken;
        return csrfToken;
      }
    } catch (err) {
      console.error('Failed to fetch CSRF token:', err);
    }
    return null;
  })();

  try {
    return await csrfInFlight;
  } finally {
    // Cleared so a later caller can retry after a failure. Callers already
    // holding this promise are unaffected, and one that arrives after a success
    // short-circuits on csrfToken above without fetching at all.
    csrfInFlight = null;
  }
}

function isStateChangingRequest(init) {
  const method = (init?.method || 'GET').toUpperCase();
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
}

// The "am I logged in?" probe AuthContext runs on mount. A 401 here is the
// normal answer for a signed-out visitor, not an expired session -- but since
// auth moved to a cookie this wrapper attaches credentials to every request,
// so the probe started coming back 401 for guests and firing the expiry
// event. That greeted every first-time visitor with a "Your session has
// expired. Please log in again." toast and an unprompted login modal.
//
// Excluded by URL rather than by "do we think someone is logged in", because
// the probe is exactly the call that answers that question -- there is no
// reliable state to consult at the moment it runs.
// Endpoints that can mint a session. Kept narrow so the response-body clone
// above never runs on ordinary traffic.
function isAuthEndpoint(resource) {
  const url = typeof resource === 'string' ? resource : resource?.url || '';
  return /\/api\/auth\/(login|login\/2fa|register|verify-otp)/.test(url);
}

function isAuthProbe(resource) {
  const url = typeof resource === 'string' ? resource : resource?.url || '';
  return url.includes('/api/auth/me');
}

export function installAuthExpiryInterceptor() {
  if (installed) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const [resource, init = {}] = args;
    
    // SECURITY: Add credentials to all requests for cookie-based auth
    if (!init.credentials) {
      init.credentials = 'include';
    }

    // Native only: the session cookie can't cross from the WebView's origin to
    // the API domain, so carry the token in a header instead. Never runs on
    // the web, where getSessionToken() returns null and the cookie does the
    // work. An Authorization header the caller set itself is left alone.
    const sessionToken = getSessionToken();
    if (sessionToken) {
      if (!init.headers) init.headers = {};
      if (typeof init.headers.set === 'function') {
        if (!init.headers.has('Authorization')) init.headers.set('Authorization', `Bearer ${sessionToken}`);
      } else if (!init.headers.Authorization) {
        init.headers.Authorization = `Bearer ${sessionToken}`;
      }
    }
    
    // SECURITY: Add CSRF token to state-changing requests
    if (isStateChangingRequest(init)) {
      const csrf = await fetchCsrfToken(originalFetch);
      if (csrf) {
        if (!init.headers) init.headers = {};
        if (typeof init.headers.set === 'function') {
          // Headers object
          init.headers.set('X-CSRF-Token', csrf);
        } else {
          // Plain object
          init.headers['X-CSRF-Token'] = csrf;
        }
      }
    }
    
    let response = await originalFetch(resource, init);

    // Pick up a token minted by any auth response (login, 2FA, OTP verify,
    // register). Reading the body costs a clone, so only auth endpoints are
    // inspected, and only in the app -- web responses never carry a token.
    if (isNativeApp() && response.ok && isAuthEndpoint(resource)) {
      const issued = await response.clone().json().then((b) => b?.token).catch(() => null);
      if (issued) rememberSessionToken(issued);
    }

    // Session expired: 401 with credentials: 'include' means token is invalid/expired
    if (response.status === 401 && init.credentials === 'include' && !isAuthProbe(resource)) {
      // A rejected token is worse than no token: it would be replayed on every
      // subsequent request and keep failing.
      clearSessionToken();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    // A cached CSRF token goes stale whenever the server restarts or rotates
    // the secret. Previously this cleared the cache but returned the 403 as-is,
    // so the user's action still failed and they had to repeat it by hand --
    // and since the cache was only refreshed *after* a failure, the very next
    // write was the one that succeeded. Refetch and replay once instead.
    //
    // Gated on the server actually saying the token was the problem: 403 is
    // also a legitimate "you may not do this" (the admin 2FA gate returns one),
    // and silently replaying those would double-submit the request.
    if (response.status === 403 && isStateChangingRequest(init) && !init.__csrfRetried) {
      const failedOnCsrf = await response.clone().json()
        .then((body) => /csrf/i.test(body?.error || ''))
        .catch(() => false);
      if (failedOnCsrf) {
        csrfToken = null;
        const fresh = await fetchCsrfToken(originalFetch);
        if (fresh) {
          if (!init.headers) init.headers = {};
          if (typeof init.headers.set === 'function') init.headers.set('X-CSRF-Token', fresh);
          else init.headers['X-CSRF-Token'] = fresh;
          // Marks this attempt so a persistently-rejecting server can't put us
          // in an endless refetch/replay loop.
          init.__csrfRetried = true;
          response = await originalFetch(resource, init);
        }
      }
    }

    return response;
  };
}
